import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/components/feature/Footer';
import ProductsBanner from './components/ProductsBanner';
import ProductFilter from './components/ProductFilter';
import ProductGrid from './components/ProductGrid';
import SeoHead from '@/components/feature/SeoHead';
import { useProducts, fetchCategories } from '@/hooks/useProducts';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const PRODUCTS_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: '产品中心 - Koorpet 猫抓板全系列',
  description: '浏览 Koorpet 全系列猫抓板产品，包括瓦楞纸猫抓板、剑麻猫抓柱、猫爬架等，天然环保材料，多款式可选。',
  url: `${siteUrl}/products`,
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: '产品中心', item: `${siteUrl}/products` },
    ],
  },
};

export default function ProductsPage() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('全部');
  const [sortBy, setSortBy] = useState('default');
  const [categories, setCategories] = useState<string[]>(['全部']);

  const { products, loading, error } = useProducts({ category: activeCategory });

  useEffect(() => {
    fetchCategories().then(setCategories);
  }, []);

  const sortedProducts = useMemo(() => {
    const list = [...products];
    if (sortBy === 'price-asc') return list.sort((a, b) => a.price - b.price);
    if (sortBy === 'price-desc') return list.sort((a, b) => b.price - a.price);
    return list;
  }, [products, sortBy]);

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <SeoHead
        title="产品中心 - Koorpet 猫抓板全系列 | 瓦楞纸猫抓板 剑麻猫抓柱"
        description="浏览 Koorpet 全系列猫抓板产品，包括瓦楞纸猫抓板、剑麻猫抓柱、猫爬架等，天然环保材料，多款式可选，10万+猫咪信赖之选。"
        keywords="猫抓板,猫抓柱,猫爬架,瓦楞纸猫抓板,剑麻猫抓柱,宠物用品"
        canonical="/products"
        ogImage="https://readdy.ai/api/search-image?query=cat%20scratching%20products%20collection%20sisal%20corrugated%20paper%20natural%20materials%20clean%20white%20background%20product%20photography&width=1200&height=630&seq=og-products-001&orientation=landscape"
        ogType="website"
        jsonLd={PRODUCTS_JSONLD}
      />
      <Navbar />

      <ProductsBanner />

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <ProductFilter
          categories={categories}
          activeCategory={activeCategory}
          onCategoryChange={setActiveCategory}
          sortBy={sortBy}
          onSortChange={setSortBy}
          totalCount={sortedProducts.length}
        />

        {error && (
          <div className="mt-8 flex items-center gap-3 px-5 py-4 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className="ri-error-warning-line"></i>
            </div>
            {error}
          </div>
        )}

        <div className="mt-8">
          <ProductGrid products={sortedProducts} loading={loading} />
        </div>

        <div className="mt-16 rounded-2xl bg-gradient-to-r from-[#3d2314] to-[#5c3520] p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-white text-xl font-bold mb-2">{t('products_cta_title')}</h3>
            <p className="text-white/60 text-sm">{t('products_cta_desc')}</p>
          </div>
          <a
            href="/contact"
            className="px-7 py-3 rounded-full bg-white text-[#3d2314] font-semibold text-sm hover:bg-white/90 transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-customer-service-line"></i>
            {t('products_cta_btn')}
          </a>
        </div>
      </main>

      <Footer />
    </div>
  );
}
